import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import SuccessPopup from "../common/SuccessPopup";
import GlobalLoader from "../common/GlobalLoader";
import BatchCard from "./batchCard";
import "../../styles/admin/institutionCourseInfo.scss";
import BatchForm from "./BatchForm";
import Cookies from "js-cookie";
import DeleteConfirmPopUp from "../common/deleteConfirmPopUp";

import {
  createInstitutionCourseBatch,
  deleteInstitutionCourseBatch,
  getAllInstitutionCourseBatch,
  updateInstitutionCourseBatch,
} from "../../helper/apicalls/institution";
import EmptyPage from "../common/emptyPage";

const InstitutionCourseInfo = () => {
  const navigate = useNavigate();
  const role = Cookies.get("role");
  const { setHeaderName, setIsShowSearch, searchQuery } =
    useOutletContext() || {};
  const [searchParams] = useSearchParams();
  const institution_id = searchParams.get("institution");
  const course_id = searchParams.get("course");
  const authToken = Cookies.get("admin_token");
  const [batches, setBatches] = useState([]);
  const [loader, setLoader] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Batch created successfully");
  const [popupType, setPopupType] = useState("success");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const location = useLocation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const title = location?.state?.courseName;

  const handleCreateClick = () => {
    setCurrentBatch(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentBatch(null);
  };

  const handleFormSubmit = async (data) => {
    setLoader(true);
    try {
      if (currentBatch) {
        const updataBatch = await updateInstitutionCourseBatch(
          authToken,
          currentBatch?.id,
          data
        );

        setSuccessMsg(
          updataBatch?.message || "Update batch is not implemented yet"
        );
        setPopupType("success");
        setSuccessOpen(true);
        await fetchInstitutionCourseBatch();
      } else {
        const payload = {
          ...data,
          institution: institution_id,
          course_key: course_id,
        };
        const res = await createInstitutionCourseBatch(authToken, payload);
        setSuccessMsg(res?.message || "Batch created successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchInstitutionCourseBatch();
      }
    } catch (err) {
      console.error("Failed to submit batch:", err);
      setSuccessMsg(err?.message || "Failed to submit batch");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      handleFormClose();
    }
  };

  const handleCardEdit = (idx) => {
    const b = batches[idx];
    if (!b) return;
    setCurrentBatch(b);
    setIsFormOpen(true);
  };

  const handleCardDelete = (batch) => {
    setDeleteTarget(batch);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setLoader(true);
    try {
      const res = await deleteInstitutionCourseBatch(
        authToken,
        deleteTarget?.id ?? deleteTarget?._id
      );
      setSuccessMsg(res?.message || "Batch deleted successfully");
      setPopupType("success");
      setSuccessOpen(true);
      await fetchInstitutionCourseBatch();
    } catch (err) {
      console.error("Failed to delete batch:", err);
      setSuccessMsg(err?.message || "Failed to delete batch");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setIsConfirmDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const fetchInstitutionCourseBatch = async () => {
    setLoader(true);
    try {
      const params = {};
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res = await getAllInstitutionCourseBatch(
        authToken,
        institution_id,
        course_id,
        params
      );
      setBatches(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
      setSuccessMsg(err?.message || "Failed to load batches");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setHeaderName(title?.charAt(0).toUpperCase() + title?.slice(1) || "Course");
    setIsShowSearch(true);
  }, []);

  useEffect(() => {
    fetchInstitutionCourseBatch();
  }, [debouncedSearch]);

  return (
    <div className="institution-course-info-container">
      {loader && <GlobalLoader visible={loader} text="Loading..." />}
      <div className="institution-course-info-header">
        <h2 style={{ margin: 0 }}>Batches List</h2>

        <button
          className="institution-course-info-create-btn btn-interactive"
          onClick={handleCreateClick}
        >
          Create Batch
        </button>
      </div>
      {batches?.length > 0 ? (
        <div className="institution-course-info-grid">
          {batches?.map((data, index) => {
            return (
              <BatchCard
                batch={data}
                index={index}
                key={index}
                onClick={() => {
                  navigate(
                    `/admin/institution/course/batch?institution=${institution_id}&batch_id=${data?.id}`,
                    { state: { batchName: data?.name } }
                  );
                }}
                onEdit={handleCardEdit}
                onDelete={handleCardDelete}
              />
            );
          })}
        </div>
      ) : (
        <EmptyPage content={"No batches Available"} />
      )}

      {isFormOpen && (
        <BatchForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={currentBatch}
        />
      )}
      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          handleDelete={handleConfirmDelete}
          title="Delete batch"
          message="Are you sure you want to delete this batch? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      {successOpen && (
        <SuccessPopup
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          message={successMsg}
          type={popupType}
        />
      )}
    </div>
  );
};

export default InstitutionCourseInfo;
